import React, { useState, useEffect, useRef } from 'react';
import { toast } from 'react-toastify';
import { BsCheckCircle, BsExclamationTriangle, BsFileText } from 'react-icons/bs';
import ModalHelp from '../../../components/ModalHelp';
import BackendErrorMessage from '../../../components/BackendErrorMessage';
import AnimatedModal from './AnimatedModal';
import EditarEditaisModal from './EditarEditaisModal';
import useUserStore from '../../../stores/userStore';
import { PermissaoEnum } from '../../../model/Usuario/Permissao';

interface EditarProcessoModalProps {
  show: boolean;
  onClose: () => void;
  processo: any;
  unidades: any[];
  onSave: (processoAtualizado: any) => Promise<void>;
  onCancel: (processoId: string) => Promise<void>;
}

const EditarProcessoModal: React.FC<EditarProcessoModalProps> = ({ 
  show, 
  onClose, 
  processo, 
  unidades, 
  onSave,
  onCancel 
}) => {
  const [formData, setFormData] = useState({
    identificador: '',
    responsavel: '',
    unidadeId: ''
  });
  const [configAcessoCandidato, setConfigAcessoCandidato] = useState({
    usarGlobal: false,
    janelaLiberacaoHoras: 3,
    envioFormularioRecursoPorEmail: true,
    prazoInterposicaoRecursoValor: 3,
    prazoInterposicaoRecursoUnidade: 'HORAS' as 'HORAS' | 'DIAS',
    envioAutomaticoResultadoPorEtapaHabilitado: false
  });
  const [contaIntegracao, setContaIntegracao] = useState<any | null>(null);
  const [loading, setLoading] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [confirmingCancel, setConfirmingCancel] = useState(false);
  const [showEditaisModal, setShowEditaisModal] = useState(false);
  const cancelTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const usuario = useUserStore((state) => state.usuario);
  const isAdmin = usuario?.perfil?.permissoes?.some((p: any) => p.nome === PermissaoEnum.ADMINISTRADOR) ?? false;

  useEffect(() => {
    if (processo) {
      setFormData({
        identificador: processo.identificador || '',
        responsavel: processo.responsavel || '',
        unidadeId: processo.unidade?.id || ''
      });
      const config = processo.configuracaoAcessoCandidato;
      setConfigAcessoCandidato({
        usarGlobal: config?.modo === 'GLOBAL',
        janelaLiberacaoHoras: config?.janelaLiberacaoHoras ?? 3,
        envioFormularioRecursoPorEmail: config?.envioFormularioRecursoPorEmail ?? true,
        prazoInterposicaoRecursoValor: config?.prazoInterposicaoRecursoValor ?? 3,
        prazoInterposicaoRecursoUnidade: config?.prazoInterposicaoRecursoUnidade ?? 'HORAS',
        envioAutomaticoResultadoPorEtapaHabilitado: config?.envioAutomaticoResultadoPorEtapaHabilitado ?? false
      });

      const conta = processo.contasServico?.find((c: any) => c.perfilNome?.startsWith('INTEGRACAO_CANDIDATOS'));
      setContaIntegracao(conta || null);
    }
    setConfirmingCancel(false);
    if (cancelTimeoutRef.current) {
      clearTimeout(cancelTimeoutRef.current);
      cancelTimeoutRef.current = null;
    }
  }, [processo]);

  if (!show) return null;

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    if (!formData.identificador.trim() || !formData.responsavel.trim() || !formData.unidadeId) {
      toast.error('Por favor, preencha todos os campos obrigatórios.');
      return;
    }
    if (!configAcessoCandidato.usarGlobal) {
      if (!Number.isFinite(configAcessoCandidato.janelaLiberacaoHoras) || configAcessoCandidato.janelaLiberacaoHoras <= 0) {
        toast.error('Informe uma janela de liberação válida (horas).');
        return;
      }
      if (!Number.isFinite(configAcessoCandidato.prazoInterposicaoRecursoValor) || configAcessoCandidato.prazoInterposicaoRecursoValor <= 0) {
        toast.error('Informe um prazo de recurso válido.');
        return;
      }
    }

    try {
      setLoading(true);
      const processoAtualizado = {
        ...processo,
        nome: formData.identificador.trim(),
        identificador: formData.identificador.trim(),
        responsavel: formData.responsavel.trim(),
        unidade: unidades.find(u => u.id === formData.unidadeId),
        configuracaoAcessoCandidato: {
          modo: configAcessoCandidato.usarGlobal ? 'GLOBAL' : 'PROCESSO',
          janelaLiberacaoHoras: configAcessoCandidato.usarGlobal ? undefined : configAcessoCandidato.janelaLiberacaoHoras,
          envioFormularioRecursoPorEmail: configAcessoCandidato.usarGlobal ? undefined : configAcessoCandidato.envioFormularioRecursoPorEmail,
          prazoInterposicaoRecursoValor: configAcessoCandidato.usarGlobal ? undefined : configAcessoCandidato.prazoInterposicaoRecursoValor,
          prazoInterposicaoRecursoUnidade: configAcessoCandidato.usarGlobal ? undefined : configAcessoCandidato.prazoInterposicaoRecursoUnidade,
          envioAutomaticoResultadoPorEtapaHabilitado: configAcessoCandidato.envioAutomaticoResultadoPorEtapaHabilitado
        }
      };
      
      await onSave(processoAtualizado);
      toast.success('Processo atualizado com sucesso!');
      onClose();
    } catch (error) {
      toast.error(BackendErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = async () => {
    if (!processo) return;

    if (!confirmingCancel) {
      setConfirmingCancel(true);
      cancelTimeoutRef.current = setTimeout(() => {
        setConfirmingCancel(false);
      }, 5000);
    } else {
      if (cancelTimeoutRef.current) {
        clearTimeout(cancelTimeoutRef.current);
      }
      try {
        setCancelLoading(true);
        await onCancel(processo.id);
        toast.success('Processo cancelado com sucesso!');
        onClose();
      } catch (error) {
        toast.error(BackendErrorMessage(error));
      } finally {
        setCancelLoading(false);
        setConfirmingCancel(false);
      }
    }
  };

  return (
    <>
    <AnimatedModal isOpen={show} onClose={onClose}>
      <div className="modal-content" style={{
        backgroundColor: 'white',
        borderRadius: '8px',
        padding: '24px',
        minWidth: '500px',
        maxWidth: '600px',
        maxHeight: '90vh',
        overflow: 'auto',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
      }}>
        <div className="modal-header" style={{
          borderBottom: '1px solid #e9ecef',
          paddingBottom: '16px',
          marginBottom: '20px'
        }}>
          <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '600' }}>
            Editar Processo
          </h3>
          <button
            onClick={onClose}
            style={{
              position: 'absolute',
              top: '16px',
              right: '16px',
              background: 'none',
              border: 'none',
              fontSize: '20px',
              cursor: 'pointer',
              color: '#6c757d'
            }}
          >
            ×
          </button>
        </div>

        <div className="modal-body" style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '16px'
        }}>
          <ModalHelp text="Edite o nome, identificador, responsável e unidade do processo. Alterações no identificador e nome são permitidas apenas enquanto o processo estiver ativo." />
          <div>
            <label style={{
              display: 'block',
              marginBottom: '8px',
              fontSize: '14px',
              fontWeight: '500',
              color: '#495057'
            }}>
              Identificador: *
            </label>
            <input
              type="text"
              value={formData.identificador}
              onChange={(e) => handleInputChange('identificador', e.target.value)}
              disabled={!processo.nomeEditavel}
              style={{
                width: '100%',
                padding: '10px 12px',
                border: '1px solid #ced4da',
                borderRadius: '4px',
                fontSize: '14px',
                backgroundColor: processo.nomeEditavel ? 'white' : '#f8f9fa',
                cursor: processo.nomeEditavel ? 'text' : 'not-allowed'
              }}
            />
          </div>

          <div>
            <label style={{
              display: 'block',
              marginBottom: '8px',
              fontSize: '14px',
              fontWeight: '500',
              color: '#495057'
            }}>
              Responsável: *
            </label>
            <input
              type="text"
              value={formData.responsavel}
              onChange={(e) => handleInputChange('responsavel', e.target.value)}
              disabled={!processo.nomeEditavel}
              style={{
                width: '100%',
                padding: '10px 12px',
                border: '1px solid #ced4da',
                borderRadius: '4px',
                fontSize: '14px',
                backgroundColor: processo.nomeEditavel ? 'white' : '#f8f9fa',
                cursor: processo.nomeEditavel ? 'text' : 'not-allowed'
              }}
            />
          </div>

          <div>
            <label style={{
              display: 'block',
              marginBottom: '8px',
              fontSize: '14px',
              fontWeight: '500',
              color: '#495057'
            }}>
              Unidade: *
            </label>
            <select
              value={formData.unidadeId}
              onChange={(e) => handleInputChange('unidadeId', e.target.value)}
              disabled={!processo.nomeEditavel}
              style={{
                width: '100%',
                padding: '10px 12px',
                border: '1px solid #ced4da',
                borderRadius: '4px',
                fontSize: '14px',
                backgroundColor: processo.nomeEditavel ? 'white' : '#f8f9fa',
                cursor: processo.nomeEditavel ? 'pointer' : 'not-allowed'
              }}
            >
              <option value="">Selecione uma unidade</option>
              {unidades.map(unidade => (
                <option key={unidade.id} value={unidade.id}>
                  {unidade.nome}
                </option>
              ))}
            </select>
          </div>

          {contaIntegracao && (
            <div>
              <label style={{
                display: 'block',
                marginBottom: '8px',
                fontSize: '14px',
                fontWeight: '500',
                color: '#495057'
              }}>
                Integração de Candidatos
              </label>
              <input
                type="text"
                value={`${contaIntegracao.descricao} (${contaIntegracao.perfilNome === 'INTEGRACAO_CANDIDATOS_COMPLETO' ? 'Completa' : 'Candidatos'})`}
                disabled
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '1px solid #ced4da',
                  borderRadius: '4px',
                  fontSize: '14px',
                  backgroundColor: '#f8f9fa',
                  cursor: 'not-allowed'
                }}
              />
              <small style={{ color: '#6c757d', fontSize: '12px' }}>Contas de serviço não podem ser editadas aqui</small>
            </div>
          )}

          <div style={{
            padding: '16px',
            borderRadius: '8px',
            border: '1px solid #dfe3e8',
            backgroundColor: '#fbfdff'
          }}>
            <div style={{ fontWeight: '600', fontSize: '14px', marginBottom: '12px', color: '#2f3d4a' }}>
              Configurações Avançadas
            </div>

            <div style={{
              border: '1px solid #b7ebc6',
              background: '#ecfdf3',
              borderRadius: '10px',
              padding: '10px 12px',
              marginBottom: '12px'
            }}>
              <div className="form-check form-switch mb-0">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="editar-usa-config-especifica-acesso-candidato"
                  disabled={!processo.nomeEditavel}
                  checked={!configAcessoCandidato.usarGlobal}
                  onChange={(e) => setConfigAcessoCandidato(prev => ({ ...prev, usarGlobal: !e.target.checked }))}
                  style={{
                    backgroundColor: !configAcessoCandidato.usarGlobal ? '#198754' : undefined,
                    borderColor: !configAcessoCandidato.usarGlobal ? '#198754' : undefined
                  }}
                />
                <label className="form-check-label d-flex align-items-center gap-2" htmlFor="editar-usa-config-especifica-acesso-candidato" style={{ fontSize: '14px' }}>
                  Usar configurações específicas para este Processo
                </label>
              </div>
            </div>

            {!configAcessoCandidato.usarGlobal && (
              <div style={{ display: 'grid', gap: '12px' }}>
                <div>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '6px', fontSize: '13px', color: '#495057' }}>
                    <i className="bi bi-clock-history"></i>
                    Janela de liberação da Sala do Processo (horas)
                  </label>
                  <input
                    type="number"
                    min={1}
                    disabled={!processo.nomeEditavel}
                    value={configAcessoCandidato.janelaLiberacaoHoras}
                    onChange={(e) => setConfigAcessoCandidato(prev => ({ ...prev, janelaLiberacaoHoras: Number(e.target.value) }))}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: '1px solid #ced4da',
                      borderRadius: '4px',
                      fontSize: '14px',
                      backgroundColor: processo.nomeEditavel ? 'white' : '#f8f9fa'
                    }}
                  />
                </div>

                <div>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '6px', fontSize: '13px', color: '#495057' }}>
                    <i className="bi bi-hourglass-split"></i>
                    Prazo para interposição de recurso
                  </label>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <input
                      type="number"
                      min={1}
                      disabled={!processo.nomeEditavel}
                      value={configAcessoCandidato.prazoInterposicaoRecursoValor}
                      onChange={(e) => setConfigAcessoCandidato(prev => ({ ...prev, prazoInterposicaoRecursoValor: Number(e.target.value) }))}
                      style={{
                        flex: 1,
                        padding: '10px 12px',
                        border: '1px solid #ced4da',
                        borderRadius: '4px',
                        fontSize: '14px',
                        backgroundColor: processo.nomeEditavel ? 'white' : '#f8f9fa'
                      }}
                    />
                    <select
                      disabled={!processo.nomeEditavel}
                      value={configAcessoCandidato.prazoInterposicaoRecursoUnidade}
                      onChange={(e) => setConfigAcessoCandidato(prev => ({ ...prev, prazoInterposicaoRecursoUnidade: e.target.value as 'HORAS' | 'DIAS' }))}
                      style={{
                        width: '130px',
                        padding: '10px 12px',
                        border: '1px solid #ced4da',
                        borderRadius: '4px',
                        fontSize: '14px',
                        backgroundColor: processo.nomeEditavel ? 'white' : '#f8f9fa'
                      }}
                    >
                      <option value="HORAS">Horas</option>
                      <option value="DIAS">Dias</option>
                    </select>
                  </div>
                </div>

                <div className="form-check form-switch">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="editar-envia-formulario-recurso-email"
                    disabled={!isAdmin && !processo.nomeEditavel}
                    checked={configAcessoCandidato.envioFormularioRecursoPorEmail}
                    onChange={(e) => setConfigAcessoCandidato(prev => ({ ...prev, envioFormularioRecursoPorEmail: e.target.checked }))}
                  />
                  <label className="form-check-label d-flex align-items-center gap-2" htmlFor="editar-envia-formulario-recurso-email" style={{ fontSize: '14px' }}>
                    <i className="bi bi-envelope-check-fill"></i>
                    Incluir no e-mail de resultado da banca o acesso ao formulário de recurso do candidato
                  </label>
                </div>

                <div className="form-check form-switch mt-1">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="editar-envio-auto-etapa"
                    disabled={!isAdmin && !processo.nomeEditavel}
                    checked={configAcessoCandidato.envioAutomaticoResultadoPorEtapaHabilitado}
                    onChange={(e) => setConfigAcessoCandidato(prev => ({ ...prev, envioAutomaticoResultadoPorEtapaHabilitado: e.target.checked }))}
                  />
                  <label className="form-check-label d-flex align-items-center gap-2" htmlFor="editar-envio-auto-etapa" style={{ fontSize: '14px' }}>
                    <i className="bi bi-send-check-fill"></i>
                    Envio de e-mails de resultado por "etapas de banca" (Após FACIAL, VIRTUAL e RECURSAL)
                    <i
                      className="bi bi-info-circle"
                      title="Os e-mails são enviados automaticamente quando todas as bancas de um tipo (FACIAL, VIRTUAL ou RECURSAL) estiverem terminadas."
                      style={{ cursor: 'help' }}
                    ></i>
                  </label>
                </div>
                <div style={{ fontSize: '12px', color: '#6c757d', lineHeight: 1.4 }}>
                  Para a janela da Sala do Processo funcionar como esperado nesta estratégia, as bancas virtuais devem ocorrer no mesmo dia.
                  Bancas em dias posteriores terão liberação do link no mesmo dia da primeira banca virtual do processo.
                </div>
              </div>
            )}
          </div>

          {processo?.editais && processo.editais.length > 0 && (
            <div style={{
              padding: '12px',
              backgroundColor: '#e7f3ff',
              border: '1px solid #0d6efd',
              borderRadius: '8px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}>
              <div>
                <div style={{ fontWeight: '500', fontSize: '14px', marginBottom: '4px' }}>
                  Editais do Processo
                </div>
                <div style={{ fontSize: '12px', color: '#6c757d' }}>
                  {processo.editais.length} edital(is) cadastrado(s)
                </div>
              </div>
              <button
                onClick={() => setShowEditaisModal(true)}
                style={{
                  padding: '8px 16px',
                  border: '1px solid #0d6efd',
                  borderRadius: '4px',
                  backgroundColor: 'white',
                  color: '#0d6efd',
                  cursor: 'pointer',
                  fontSize: '14px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  fontWeight: '500'
                }}
              >
                <BsFileText /> Gerenciar Editais
              </button>
            </div>
          )}
        </div>

        <div className="modal-footer" style={{
          borderTop: '1px solid #e9ecef',
          paddingTop: '16px',
          marginTop: '20px',
          display: 'flex',
          justifyContent: 'space-between'
        }}>
          <button
            onClick={handleCancel}
            disabled={loading || cancelLoading}
            style={{
              padding: '8px 16px',
              border: '1px solid #dc3545',
              borderRadius: '4px',
              backgroundColor: loading || cancelLoading ? '#6c757d' : (confirmingCancel ? '#dc3545' : 'tomato'),
              color: 'white',
              cursor: loading || cancelLoading ? 'not-allowed' : 'pointer',
              fontSize: '14px',
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}
            title={confirmingCancel ? "O cancelamento não poderá ser revertido." : "Processo pode ser cancelado em seus estágios iniciais"}
          >
            {cancelLoading ? (
              'Cancelando...'
            ) : confirmingCancel ? (
              <>
                <BsExclamationTriangle /> Confirma cancelamento?
              </>
            ) : (
              <>
                <BsCheckCircle /> Cancelar Processo
              </>
            )}
          </button>
          
          <div style={{ display: 'flex', gap: '12px' }}>
            <button
              onClick={onClose}
              disabled={loading || cancelLoading}
              style={{
                padding: '8px 16px',
                border: '1px solid #6c757d',
                borderRadius: '4px',
                backgroundColor: 'white',
                color: '#6c757d',
                cursor: loading || cancelLoading ? 'not-allowed' : 'pointer',
                fontSize: '14px'
              }}
            >
              Fechar
            </button>
            {(isAdmin ? (processo.status !== 'Finalizado' && processo.status !== 'Cancelado') : processo.nomeEditavel) && (
              <button
                onClick={handleSave}
                disabled={loading || cancelLoading}
                style={{
                  padding: '8px 16px',
                  border: '1px solid #0d6efd',
                  borderRadius: '4px',
                  backgroundColor: loading ? '#6c757d' : '#0d6efd',
                  color: 'white',
                  cursor: loading || cancelLoading ? 'not-allowed' : 'pointer',
                  fontSize: '14px'
                }}
              >
                {loading ? 'Salvando...' : 'Salvar'}
              </button>
            )}
          </div>
        </div>
      </div>
    </AnimatedModal>
    
    {showEditaisModal && (
      <EditarEditaisModal
        show={showEditaisModal}
        onClose={() => setShowEditaisModal(false)}
        processo={processo}
        onSuccess={() => {
          window.location.reload();
        }}
      />
    )}
    </>
  );
};

export default EditarProcessoModal;
